import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Maximize2, FileText, Ruler, Box, AlertCircle } from "lucide-react";
import Lightbox from "./Lightbox";
import { DELIVERABLES_NOTE } from "../data/studies";
import { setPrefill } from "../lib/prefill";
import "./StudyModal.css";

const KIND_ICON = { photo: FileText, cao: Box, plan: Ruler, compare: Box };

const StudyModal = ({ study, onClose }) => {
  const [lightbox, setLightbox] = useState(null);
  const closeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && lightbox === null && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      previous?.focus?.();
    };
  }, [onClose, lightbox]);

  const discuss = () => {
    setPrefill({
      typeBesoin: study.contactType,
      description: `Besoin similaire à l'étude ${study.num} — ${study.title} (${study.subtitle}).\n\n`,
    });
    onClose();
    navigate("/#contact");
  };

  const items = study.visuals.map((v) => ({ src: v.src, alt: v.alt, label: `${study.num} · ${v.label}`, note: v.note, caption: `${study.title} — ${study.subtitle}` }));

  return (
    <div className="sm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="study-modal">
      <div
        className="sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`sm-title-${study.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-head">
          <div>
            <p className="s-label">Étude {study.num} · {study.nature}</p>
            <h2 id={`sm-title-${study.id}`} className="sm-title">{study.title}</h2>
            <p className="sm-subtitle">{study.subtitle}</p>
          </div>
          <button type="button" className="sm-close" ref={closeRef} onClick={onClose} aria-label="Fermer la fiche" data-testid="study-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="sm-body">
          {study.visualWarning && <p className="sm-missing" data-testid="study-visual-warning"><AlertCircle size={15} /> {study.visualWarning}</p>}
          <section className={`sm-visuals ${study.visuals.length === 1 ? "sm-visuals-single" : ""}`} aria-label="Visuels disponibles">
            {study.visuals.map((v, i) => {
              const Icon = KIND_ICON[v.kind] || Box;
              return (
                <button
                  type="button"
                  key={v.label}
                  className={`sm-visual sm-visual-${v.kind}`}
                  onClick={() => setLightbox(i)}
                  aria-label={`Agrandir : ${v.label}`}
                  data-testid={`study-visual-${i}`}
                >
                  <span className="sm-visual-frame" style={study.visuals.length === 1 ? { aspectRatio: `${v.width} / ${v.height}` } : undefined}>
                    <img src={v.src} srcSet={v.srcSet} sizes="(max-width: 640px) 90vw, 360px" alt={v.alt} loading="lazy" width={v.width} height={v.height} />
                    <span className="sm-visual-zoom"><Maximize2 size={14} /></span>
                  </span>
                  <span className="sm-visual-meta">
                    <span className="sm-visual-label"><Icon size={13} /> {v.label}</span>
                    <span className="sm-visual-note">{v.note}</span>
                  </span>
                </button>
              );
            })}
          </section>

          <div className="sm-columns">
            <section className="sm-block">
              <h3>Le besoin</h3>
              <p>{study.need}</p>
            </section>
            <section className="sm-block">
              <h3>{study.workHeading || "Le travail effectué par SI2T"}</h3>
              <p>{study.work}</p>
            </section>
          </div>

          <section className="sm-block">
            <h3>Déroulé de l'étude</h3>
            <ol className="sm-steps">
              {study.steps.map((s, i) => (
                <li key={s.label}>
                  <span className="sm-step-num">0{i + 1}</span>
                  <div>
                    <strong>{s.label}</strong>
                    <span>{s.text}</span>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="sm-block">
            <h3>{study.deliverablesLabel || "Livrables"}</h3>
            <ul className="sm-deliverables" aria-label={study.deliverablesLabel || "Livrables"}>
              {study.deliverables.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <p className="sm-note">{study.deliverablesNote || DELIVERABLES_NOTE}</p>
          </section>

          {study.missing && (
            <p className="sm-missing" data-testid="study-missing">
              <AlertCircle size={15} /> {study.missing}
            </p>
          )}
        </div>

        <div className="sm-foot">
          <button type="button" className="btn btn-primary" onClick={discuss} data-testid="study-discuss-cta">
            Discuter d'un besoin similaire
            <ArrowRight size={16} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      {lightbox !== null && <Lightbox items={items} index={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default StudyModal;
